import type { Contact } from '@secretario/shared';
import { PATIENT_STATUS_LABEL } from '@secretario/shared';
import { PATIENT_FILTER, usePatientLinks } from '../lib/patientLinks.js';
import { isNativeShell } from '../lib/platform.js';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'wouter';
import { ActionSheet, type SheetAction } from '../components/chrome/ActionSheet.js';
import { ensureMatrixClient } from '../lib/matrix/client.js';
import { startDirectMessage } from '../lib/matrix/rooms.js';
import { ASSISTANT_ROUTE } from '../lib/assistantBus.js';
import { useLongPress } from '../lib/useLongPress.js';
import { describeError } from '../lib/api/http.js';
import { createContact, getContactCategoryCounts, getContacts } from '../lib/api/personal.js';
import { catColor, catInfo, readCustomCategories } from '../lib/contactCategories.js';
import { useProfile } from '../lib/useProfile.js';
import './ported/contatos.css';
import './ported/contatos-extra.css';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { ImportContactsSheet } from '../components/contacts/ImportContactsSheet.js';
import { PersonAvatar, PersonGlyph } from '../components/chrome/PersonAvatar.js';
import { ContactPhoto } from '../components/chrome/PersonPhoto.js';
import { IconBookDown, IconUserPlus, PageActions } from '../components/chrome/icons.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import { paneMemory, rememberPane } from '../lib/stage/peopleSheet.js';

const PAGE_SIZE = 60;

function formatWhen(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function NewContactSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      createContact(
        phone.trim()
          ? { displayName: name.trim(), phone: phone.trim() }
          : { displayName: name.trim() },
      ),
    onSuccess: onSaved,
    onError: (err) => setError(describeError(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || mutation.isPending) return;
    setError('');
    mutation.mutate();
  }

  return createPortal(
    <div className="ct-sheetwrap" onClick={onClose}>
      <form className="ct-sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="ct-grip" />
        <div className="ct-sheet-h">Novo contato</div>
        <label className="ct-flbl" htmlFor="ct-name">
          Nome
        </label>
        <input
          id="ct-name"
          className="ct-sheet-in"
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          placeholder="Como essa pessoa se chama"
          autoFocus
        />
        <label className="ct-flbl" htmlFor="ct-phone">
          Celular <i>opcional</i>
        </label>
        <input
          id="ct-phone"
          className="ct-sheet-in"
          value={phone}
          inputMode="tel"
          maxLength={32}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="(11) 99999-9999"
        />
        {error && <p className="ct-sheet-err">{error}</p>}
        <button className="ct-savebtn" type="submit" disabled={mutation.isPending || !name.trim()}>
          {mutation.isPending ? 'Salvando…' : 'Adicionar'}
        </button>
        <button className="ct-cancel" type="button" onClick={onClose}>
          Cancelar
        </button>
      </form>
    </div>,
    document.body,
  );
}

/**
 * Lista de contatos. Inteira (rota própria) ou embutida na folha de Pessoas do
 * palco (`embedded`): sem cabeçalho nem título, com a busca e o filtro lembrados.
 */
const ChatGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 18.5V7.5A2.5 2.5 0 0 1 7.5 5h9A2.5 2.5 0 0 1 19 7.5v6a2.5 2.5 0 0 1-2.5 2.5H9l-4 2.5Z" />
  </svg>
);
const CallGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6.5 4h3l1.5 4-2 1.3a10 10 0 0 0 5.7 5.7L16 13l4 1.5v3A2 2 0 0 1 18 19.5 15 15 0 0 1 4.5 6 2 2 0 0 1 6.5 4Z" />
  </svg>
);
const SecretaryGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3.5 13.8 8l4.7.4-3.6 3 1.1 4.6L12 13.6 8 16l1.1-4.6-3.6-3 4.7-.4L12 3.5Z" />
  </svg>
);

export function ContatosPage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  // Segurar um contato: mandar mensagem, ligar ou pedir algo ao Secretário sobre ele.
  const [acting, setActing] = useState<Contact | null>(null);
  const hold = useLongPress<Contact>((contact) => setActing(contact));
  function actionsFor(contact: Contact): SheetAction[] {
    const name = contact.preferredName || contact.displayName || 'este contato';
    // Primeiro nome de verdade (a agenda tem nomes como ".. Lead 2609"): a primeira palavra com letras.
    const first = name.split(/\s+/).find((word) => /\p{L}{2,}/u.test(word)) ?? 'este contato';
    const actions: SheetAction[] = [];
    if (contact.appUsername) {
      const username = contact.appUsername;
      actions.push({
        key: 'mensagem',
        label: 'Mandar mensagem',
        hint: 'Conversa protegida no Secretário',
        icon: <ChatGlyph />,
        onSelect: () =>
          void ensureMatrixClient().then(async (client) => {
            if (!client) return;
            const roomId = await startDirectMessage(client, `@${username}:${client.getDomain()}`);
            navigate(`/conversas/${encodeURIComponent(roomId)}`);
          }),
      });
    }
    if (contact.phone) {
      const digits = contact.phone.replace(/[^\d+]/g, '');
      actions.push({
        key: 'ligar',
        label: 'Ligar',
        hint: contact.phone,
        icon: <CallGlyph />,
        onSelect: () => {
          window.location.href = `tel:${digits}`;
        },
      });
    }
    actions.push({
      key: 'secretario',
      label: `Pedir algo sobre ${first}`,
      hint: 'Lembrete, mensagem pronta, data importante…',
      icon: <SecretaryGlyph />,
      onSelect: () =>
        navigate(`${ASSISTANT_ROUTE}?pedido=${encodeURIComponent(`Sobre ${name}: `)}`),
    });
    return actions;
  }
  const [filter, setFilter] = useState<string>(() =>
    embedded ? paneMemory('contatos').filter : 'all',
  );
  const [searchInput, setSearchInput] = useState(() =>
    embedded ? paneMemory('contatos').query : '',
  );
  const [search, setSearch] = useState(searchInput);
  useEffect(() => {
    if (embedded) rememberPane('contatos', { query: searchInput, filter });
  }, [embedded, searchInput, filter]);
  const [adding, setAdding] = useState(false);
  // Atalho da primeira entrega (/contatos?importar=1): a importação já abre.
  const [importing, setImporting] = useState(
    () => isNativeShell() && new URLSearchParams(window.location.search).get('importar') === '1',
  );

  // Debounce: espera 250ms sem digitar antes de buscar no servidor.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const profileQ = useProfile();
  const customCategories = useMemo(
    () => readCustomCategories(profileQ.data?.preferences),
    [profileQ.data],
  );

  // Contagens reais por categoria (independem da paginação/busca da lista abaixo).
  const countsQ = useQuery({
    queryKey: ['contacts-category-counts'],
    queryFn: getContactCategoryCounts,
    staleTime: 20_000,
  });
  const categories = useMemo(
    () => [...(countsQ.data?.categories ?? [])].sort((a, b) => b.count - a.count),
    [countsQ.data],
  );
  const total = countsQ.data?.total ?? 0;

  // Lista paginada: filtro e busca aplicados no servidor. Buscando, o servidor
  // devolve até 50 resultados de uma vez (sem paginação).
  const patientLinks = usePatientLinks();
  const showPatients = filter === PATIENT_FILTER;
  const contactsQ = useInfiniteQuery({
    enabled: !showPatients,
    queryKey: ['contacts-page', filter, search],
    queryFn: ({ pageParam }) =>
      getContacts({ category: filter, search, page: pageParam, pageSize: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (search) return undefined;
      const loaded = lastPage.page * lastPage.pageSize;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    staleTime: 15_000,
  });

  const contacts: Contact[] = contactsQ.data?.pages.flatMap((p) => p.data) ?? [];
  const visibleTotal = contactsQ.data?.pages[0]?.total ?? 0;

  function refreshAll() {
    setAdding(false);
    void qc.invalidateQueries({ queryKey: ['contacts-page'] });
    void qc.invalidateQueries({ queryKey: ['contacts-category-counts'] });
  }

  return (
    <div className={embedded ? 'ct ct-embedded' : 'ct'}>
      <div className="ct-wrap">
        {!embedded && (
          <>
            <header className="ct-hd">
              <Link href="/" className="ct-back back-btn" aria-label="Voltar para a tela inicial">
                <BackIcon />
              </Link>
              <PageActions>
                {isNativeShell() && (
                  <button
                    className="icon-btn"
                    aria-label="Trazer da agenda do aparelho"
                    onClick={() => setImporting(true)}
                  >
                    <IconBookDown />
                  </button>
                )}
                <button
                  className="icon-btn primary"
                  aria-label="Adicionar contato"
                  onClick={() => setAdding(true)}
                >
                  <IconUserPlus />
                </button>
              </PageActions>
            </header>

            <div className="ct-kicker">Sua rede</div>
            <h1 className="ct-h1">Contatos</h1>
            <p className="ct-sub">
              {total === 1
                ? '1 pessoa que o seu secretário conhece e organiza por você.'
                : `${total} pessoas que o seu secretário conhece e organiza por você.`}
            </p>
          </>
        )}

        <div className="ct-tools">
          <div className="ct-search">
            <svg
              className="ct-search-ic"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
              <line
                x1="16.2"
                y1="16.2"
                x2="21"
                y2="21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              className="ct-search-input"
              type="text"
              inputMode="search"
              placeholder="Buscar por nome ou telefone…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                className="ct-search-clear"
                aria-label="Limpar busca"
                onClick={() => setSearchInput('')}
              >
                ×
              </button>
            )}
          </div>
          {embedded && (
            <>
              {isNativeShell() && (
                <button
                  className="icon-btn ct-toolbtn"
                  aria-label="Trazer da agenda do aparelho"
                  onClick={() => setImporting(true)}
                >
                  <IconBookDown />
                </button>
              )}
              <button
                className="icon-btn primary ct-toolbtn"
                aria-label="Adicionar contato"
                onClick={() => setAdding(true)}
              >
                <IconUserPlus />
              </button>
            </>
          )}
        </div>

        <div className="ct-chips">
          <button
            className={`ct-chip${filter === 'all' ? ' on' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos <i>{total}</i>
          </button>
          {categories.map(({ category, count }) => (
            <button
              key={category}
              className={`ct-chip${filter === category ? ' on' : ''}`}
              style={
                { ['--cc' as string]: catInfo(category, customCategories).c } as React.CSSProperties
              }
              onClick={() => setFilter(category)}
            >
              {catInfo(category, customCategories).label} <i>{count}</i>
            </button>
          ))}
          {patientLinks.list.length > 0 && (
            <button
              className={`ct-chip${showPatients ? ' on' : ''}`}
              style={{ ['--cc' as string]: '#C24E29' } as React.CSSProperties}
              onClick={() => setFilter(PATIENT_FILTER)}
            >
              Pacientes <i>{patientLinks.list.length}</i>
            </button>
          )}
        </div>

        {showPatients && (
          <div className="ct-list">
            {patientLinks.list
              .filter((row) =>
                search
                  ? `${row.contactName} ${row.patientName}`
                      .toLowerCase()
                      .includes(search.toLowerCase())
                  : true,
              )
              .map((row) => (
                <Link key={row.contactId} href={`/contatos/${row.contactId}`} className="ct-card">
                  <PersonAvatar className="ct-av" name={row.contactName} color="#C24E29" />
                  <span className="ct-info">
                    <b>{row.contactName}</b>
                    <small>
                      <span
                        className="ct-badge"
                        style={{ ['--cc' as string]: '#C24E29' } as React.CSSProperties}
                      >
                        Paciente
                      </span>
                      {` · ${row.clinicName} · ${PATIENT_STATUS_LABEL[row.status]}`}
                    </small>
                  </span>
                  <span className="ct-arrow">›</span>
                </Link>
              ))}
            <p className="ct-pnote">
              Vêm da {patientLinks.list[0]?.clinicName ?? 'clínica'}, pelo telefone. Para mudar,
              abra a ficha na clínica.
            </p>
          </div>
        )}

        {showPatients ? null : contactsQ.isLoading ? (
          <div className="ct-loading">
            <Skeleton variant="person" label="Abrindo os contatos" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="ct-empty">
            <i className="ct-empty-ic" aria-hidden="true">
              <PersonGlyph />
            </i>
            {search ? (
              <>
                <b>Ninguém encontrado</b>
                <span>Não achei nenhum contato com "{search}". Tenta outro nome ou número.</span>
              </>
            ) : (
              <>
                <b>Nada por aqui ainda</b>
                {isNativeShell() ? (
                  <>
                    <span>
                      Traga sua agenda e veja quem já está no Secretário. Você escolhe quem entra, e
                      nada sai do aparelho antes de você confirmar.
                    </span>
                    <button className="ct-resync" onClick={() => setImporting(true)}>
                      Trazer da agenda
                    </button>
                    <button className="ct-more" onClick={() => setAdding(true)}>
                      Adicionar um por um
                    </button>
                  </>
                ) : (
                  <>
                    <span>
                      Quando alguém falar com você, seu secretário organiza aqui. Você também pode
                      adicionar quem quiser.
                    </span>
                    <button className="ct-resync" onClick={() => setAdding(true)}>
                      Adicionar contato
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="ct-list sk-cascade">
              {contacts.map((c) => {
                const info = catInfo(c.category, customCategories);
                const accent = { ['--cc' as string]: info.c } as React.CSSProperties;
                return (
                  <Link key={c.id} href={`/contatos/${c.id}`} className="ct-card" {...hold(c)}>
                    <PersonAvatar
                      className="ct-av"
                      name={c.displayName}
                      color={catColor(c.category, customCategories)}
                    >
                      {c.appUsername && <ContactPhoto username={c.appUsername} />}
                    </PersonAvatar>
                    <span className="ct-info">
                      <b>{c.displayName || 'Sem nome'}</b>
                      <small>
                        <span className="ct-badge" style={accent}>
                          {info.label}
                        </span>
                        {c.appUsername && <span className="ct-onapp">no Secretário</span>}
                        {patientLinks.byContact.has(c.id) && (
                          <span className="ct-onapp">também paciente</span>
                        )}
                        {c.subcategory ? ` · ${c.subcategory}` : ''}
                        {formatWhen(c.lastInteractionAt)
                          ? ` · ${formatWhen(c.lastInteractionAt)}`
                          : ''}
                      </small>
                    </span>
                    <span className="ct-arrow">›</span>
                  </Link>
                );
              })}
            </div>

            {contactsQ.hasNextPage && (
              <button
                className="ct-more"
                onClick={() => void contactsQ.fetchNextPage()}
                disabled={contactsQ.isFetchingNextPage}
              >
                {contactsQ.isFetchingNextPage
                  ? 'Carregando…'
                  : `Carregar mais (${contacts.length} de ${visibleTotal})`}
              </button>
            )}
            <button className="ct-more ct-addmore" onClick={() => setAdding(true)}>
              + Adicionar contato
            </button>
            {isNativeShell() && (
              <button className="ct-more" onClick={() => setImporting(true)}>
                Trazer da agenda do aparelho
              </button>
            )}
          </>
        )}
      </div>
      {adding && <NewContactSheet onClose={() => setAdding(false)} onSaved={refreshAll} />}
      {acting && (
        <ActionSheet
          title={acting.preferredName || acting.displayName || 'Sem nome'}
          subtitle={acting.appUsername ? 'Está no Secretário' : (acting.phone ?? 'Contato')}
          avatar={
            <PersonAvatar
              className="acts-person"
              name={acting.displayName}
              color={catColor(acting.category, customCategories)}
            >
              {acting.appUsername && <ContactPhoto username={acting.appUsername} />}
            </PersonAvatar>
          }
          actions={actionsFor(acting)}
          onClose={() => setActing(null)}
        />
      )}
      {importing && (
        <ImportContactsSheet onClose={() => setImporting(false)} onSaved={refreshAll} />
      )}
    </div>
  );
}
